import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Shield, Smartphone, Key, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user, token, fetchUser } = useContext(AuthContext);
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [smsPhone, setSmsPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [twoFaStatus, setTwoFaStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  // Fetch 2FA status on mount
  useEffect(() => {
    const fetch2FAStatus = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/auth/2fa-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTwoFaStatus(response.data);
      } catch (error) {
        console.error('Failed to fetch 2FA status:', error);
      } finally {
        setLoadingStatus(false);
      }
    };
    fetch2FAStatus();
  }, [token]);

  const setupTotp = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/setup-totp`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTotpSetup(response.data);
      toast.success('Scan QR code with your authenticator app');
    } catch (error) {
      toast.error('Failed to setup TOTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyTotp = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/verify-totp?code=${totpCode}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('TOTP enabled successfully');
      setTotpSetup(null);
      setTotpCode('');
      fetchUser();
    } catch (error) {
      toast.error('Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const setupSms = async () => {
    if (!smsPhone) {
      toast.error('Please enter phone number');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/setup-sms?phone=${encodeURIComponent(smsPhone)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Phone number saved successfully');
      setSmsPhone('');
      fetchUser();
      // Refresh 2FA status
      const response = await axios.get(`${API}/auth/2fa-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTwoFaStatus(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save phone number');
    } finally {
      setLoading(false);
    }
  };

  const enableSms = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/enable-sms`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('SMS two-factor authentication enabled');
      fetchUser();
      // Refresh 2FA status
      const response = await axios.get(`${API}/auth/2fa-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTwoFaStatus(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enable SMS 2FA');
    } finally {
      setLoading(false);
    }
  };

  const disableSms = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/disable-sms`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('SMS two-factor authentication disabled');
      fetchUser();
      // Refresh 2FA status
      const response = await axios.get(`${API}/auth/2fa-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTwoFaStatus(response.data);
    } catch (error) {
      toast.error('Failed to disable SMS 2FA');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="profile-page">
        <div>
          <h1 className="text-4xl font-bold text-primary mb-2" data-testid="profile-title">Profile & Security</h1>
          <p className="text-muted-foreground">Manage your account settings and two-factor authentication</p>
        </div>

        <Card data-testid="user-info-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Shield className="h-5 w-5 mr-2 text-accent" />
              Account Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Email</Label>
              <p className="text-lg" data-testid="user-email">{user?.email}</p>
            </div>
            <div>
              <Label>Role</Label>
              <p className="text-lg capitalize" data-testid="user-role">{user?.role?.replace('_', ' ')}</p>
            </div>
            <div>
              <Label>Two-Factor Authentication</Label>
              <div className="flex gap-2 mt-2">
                <Badge variant={user?.is_totp_enabled ? 'default' : 'secondary'} data-testid="totp-status">
                  TOTP: {user?.is_totp_enabled ? 'Enabled' : 'Disabled'}
                </Badge>
                <Badge variant={user?.is_sms_enabled ? 'default' : 'secondary'} data-testid="sms-status">
                  SMS: {user?.is_sms_enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="totp-setup-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Key className="h-5 w-5 mr-2 text-accent" />
              Authenticator App (TOTP)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {user?.is_totp_enabled ? (
              <p className="text-success" data-testid="totp-enabled-message">✓ TOTP is enabled for your account</p>
            ) : totpSetup ? (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <QRCodeSVG value={totpSetup.qr_code} size={200} data-testid="totp-qr-code" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="totp-code">Enter 6-digit code to verify</Label>
                  <Input
                    id="totp-code"
                    type="text"
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    maxLength={6}
                    data-testid="totp-verify-input"
                  />
                </div>
                <Button onClick={verifyTotp} disabled={loading || totpCode.length !== 6} data-testid="totp-verify-button">
                  Verify & Enable TOTP
                </Button>
              </div>
            ) : (
              <Button onClick={setupTotp} disabled={loading} data-testid="setup-totp-button">
                Setup Authenticator App
              </Button>
            )}
          </CardContent>
        </Card>

        <Card data-testid="sms-setup-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Smartphone className="h-5 w-5 mr-2 text-accent" />
              SMS Two-Factor
            </CardTitle>
            <CardDescription>
              Receive verification codes via SMS when logging in
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Service Status Indicator */}
            {loadingStatus ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Checking SMS service status...
              </div>
            ) : twoFaStatus?.sms_service_available ? (
              <div className="flex items-center gap-2 text-green-600 bg-green-50 p-2 rounded" data-testid="sms-service-available">
                <CheckCircle className="h-4 w-4" />
                SMS service is configured and available
              </div>
            ) : (
              <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-2 rounded" data-testid="sms-service-unavailable">
                <AlertCircle className="h-4 w-4" />
                SMS service is not configured. Contact your administrator to set up Twilio in Admin Settings.
              </div>
            )}

            {/* Phone Number Setup */}
            {user?.sms_phone || twoFaStatus?.sms_phone ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2" data-testid="sms-phone-saved">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>Phone number saved: <strong>{user?.sms_phone || twoFaStatus?.sms_phone}</strong></span>
                </div>
                
                {/* Enable/Disable SMS 2FA */}
                {user?.is_sms_enabled || twoFaStatus?.sms_enabled ? (
                  <div className="space-y-2">
                    <p className="text-green-600 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      SMS two-factor authentication is enabled
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={disableSms} 
                      disabled={loading}
                      data-testid="disable-sms-button"
                    >
                      {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Disable SMS 2FA
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Button 
                      onClick={enableSms} 
                      disabled={loading || !twoFaStatus?.sms_service_available}
                      data-testid="enable-sms-button"
                    >
                      {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Enable SMS 2FA
                    </Button>
                    {!twoFaStatus?.sms_service_available && (
                      <p className="text-xs text-amber-600">
                        SMS service must be configured before enabling
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="sms-phone">Phone Number (E.164 format)</Label>
                  <Input
                    id="sms-phone"
                    type="tel"
                    placeholder="+1234567890"
                    value={smsPhone}
                    onChange={(e) => setSmsPhone(e.target.value)}
                    data-testid="sms-phone-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter your phone number in international format (e.g., +1 for US)
                  </p>
                </div>
                <Button 
                  onClick={setupSms} 
                  disabled={loading || !twoFaStatus?.sms_service_available}
                  data-testid="setup-sms-button"
                >
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Save Phone Number
                </Button>
                {!twoFaStatus?.sms_service_available && (
                  <p className="text-xs text-amber-600">
                    SMS service must be configured by an administrator before setting up SMS 2FA
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
